import { translateInComponent } from 'infras/i18n';
import translations from '../i18n.json';
import { ReactNode, memo, useCallback, useMemo } from 'react';
import { gray04 } from 'presentations/views/styles/color';
import { DateTime, formatToTimeShort } from 'infras/datetime';
import styled from '@emotion/styled';
import EventCell from '../EventCell';
import { useDayTimeGrid } from './hooks';
import { DraggableCell, NowBorder, NowBorderCircle, TimeCell } from './Slot';
import {
  Behavior,
  CalendarSource,
  MergeableEventListItem,
  Schedule,
} from '../reducer';
import { spacing } from 'presentations/views/styles';
import Icon from 'presentations/views/components/atoms/Icon';
import { findVideoServiceType } from 'domains/models/calendar/videoServiceType';
import { formatVideoServiceTypeName } from 'presentations/views/helpers/formatter';

export const calendarNewEventPrefix = 'js-selected-slot-group-';

interface Props {
  className?: string;
  startedAt: DateTime;
  onClickEvent?: (event: MergeableEventListItem, el: HTMLElement) => void;
  selectedEventId?: string;
  onUpdateSchedule?: (schedule: Schedule) => void;
}

const Component = memo((props: Props) => {
  const t = translateInComponent(translations);
  const { state, actions } = useDayTimeGrid({
    startedAt: props.startedAt,
    onClickEvent: props.onClickEvent,
  });

  const renderNowBorder = useCallback(
    (date: DateTime) => {
      const unixtime = state.now.toSeconds();
      const diff = unixtime - date.toSeconds();
      const diffRatio = diff / 60 / state.rulerStep;
      if (0 <= diffRatio && diffRatio < 1) {
        return (
          <NowBorder ratio={diffRatio}>
            <NowBorderCircle />
          </NowBorder>
        );
      }
      return null;
    },
    [state.rulerStep, state.now],
  );

  const getLocation = useCallback((event: MergeableEventListItem) => {
    const nodes: ReactNode[] = [];
    const videoServices = findVideoServiceType(event.url, event.description);
    if (videoServices.length > 0) {
      nodes.push(
        <Video key={1}>
          <VideoIcon name="video-outline" />
          {videoServices.map((videoService, i) => (
            <VideoName key={i}>
              {formatVideoServiceTypeName(videoService.videoServiceType)}
            </VideoName>
          ))}
        </Video>,
      );
    } else if (event.address) {
      nodes.push(<Address key={1}>{event.address}</Address>);
    }
    return nodes;
  }, []);

  return (
    <Calendar
      className={props.className}
      maxRow={state.maxRow}
      maxCol={state.dates.length}
    >
      {!state.creatingEvent &&
        !state.dragDirection &&
        state.draggingGhostEvent &&
        !state.draggingGhostEvent.isAllDay && (
          <GhostEventCell
            x={state.draggingGhostEvent.x}
            y={state.draggingGhostEvent.y}
            ySpan={state.draggingGhostEvent.ySpan}
            xRatio={state.draggingGhostEvent.xRatio}
            xSpanRatio={state.draggingGhostEvent.xSpanRatio}
            xMargin={state.draggingGhostEvent.xMargin}
            name={state.draggingGhostEvent.name}
            timeText={formatToTimeShort(state.draggingGhostEvent.startedAt)}
            color={state.draggingGhostEvent.color}
            colors={(state.draggingGhostEvent.mergedEvents || []).map(
              (event) => event.color,
            )}
            icon={
              state.draggingGhostEvent.behavior === Behavior.Work && (
                <ServiceIcon name="check-circle" />
              )
            }
          />
        )}
      {!state.creatingEvent && state.currentDragGroup && (
        <DraggingEventCell
          isDraggable
          x={state.currentDragGroup.x}
          y={state.currentDragGroup.y}
          ySpan={state.currentDragGroup.ySpan}
          xRatio={0}
          xSpanRatio={1}
          xMargin={0}
          color={state.userColor}
          name={t('New Event')}
          timeText={formatToTimeShort(state.currentDragGroup.startedAt)}
        />
      )}
      {/* {state.creatingEvent && !state.creatingEvent.isAllDay && (
        <NewEventCell
          isDraggable
          className={`${calendarNewEventPrefix}${state.creatingEvent.x!}-${state
            .creatingEvent.y!}`}
          eventId={'new-event'}
          data-event-id={'new-event'}
          isDragging={'new-event' === state.draggingEvent?.uid}
          x={state.creatingEvent.x!}
          y={state.creatingEvent.y!}
          ySpan={state.creatingEvent.ySpan!}
          xRatio={0}
          xSpanRatio={1}
          xMargin={0}
          color={state.userColor}
          name={state.creatingEvent.name}
          timeText={formatToTimeShort(state.creatingEvent.startedAt)}
          onDragStart={actions.onEventDragStart}
          onDragEnd={actions.onEventDragEnd}
          onDragHandlerMove={actions.onEventDragHandlerMove}
        />
      )} */}
      {state.draggingEvent && !state.draggingEvent.isAllDay && (
        <DragEventCell
          x={state.draggingEvent.x}
          y={state.draggingEvent.y}
          ySpan={state.draggingEvent.ySpan}
          xRatio={0}
          xSpanRatio={1}
          xMargin={0}
          name={state.draggingEvent.name}
          timeText={formatToTimeShort(state.draggingEvent.startedAt)}
          color={state.draggingEvent.color}
          colors={(state.draggingEvent.mergedEvents || []).map(
            (event) => event.color,
          )}
          icon={
            state.draggingEvent.behavior === Behavior.Work && (
              <ServiceIcon name="check-circle" />
            )
          }
        />
      )}
      {!state.isLoading &&
        state.events.map((event) => (
          <StyledEventCell
            key={event.layoutId}
            isDraggable={event.calendarSource === CalendarSource.Jicoo}
            className={
              event.uid === 'new-event'
                ? `${calendarNewEventPrefix}`
                : 'js-events'
            }
            eventId={event.uid}
            isDragging={event.uid === state.draggingEvent?.uid}
            x={event.x}
            y={event.y}
            ySpan={event.ySpan}
            xRatio={event.xRatio}
            xSpanRatio={event.xSpanRatio}
            xMargin={event.xMargin}
            color={event.color}
            colors={(event.mergedEvents || []).map((event) => event.color)}
            name={event.name}
            responseStatus={event.responseStatus}
            timeText={formatToTimeShort(event.startedAt)}
            onClick={actions.onClickEvent}
            onDragStart={actions.onEventDragStart}
            onDragEnd={actions.onEventDragEnd}
            onDragHandlerMove={actions.onEventDragHandlerMove}
            isSelected={
              props.selectedEventId === event.uid || event.uid === 'new-event'
            }
            location={getLocation(event)}
            icon={
              event.behavior === Behavior.Work && (
                <ServiceIcon name="check-circle" />
              )
            }
            // accessory={event.behavior !== Behavior.Work && (
            //   <ServiceIcon name="check-circle" />
            // )}
          />
        ))}
      {state.frames.map((frame: { date: DateTime }, i: number) => {
        return (
          <EventTimeCell
            key={i}
            isTop={frame.date.hour === 0}
            isDivider={frame.date.minute === 0}
            isShowBorder={frame.date.minute % state.rulerBorderStep === 0}
            isHoliday={[0, 6].includes(frame.date.weekday % 7)}
          >
            <DraggableCell
              draggable
              data-frame-id={i}
              data-all-day={0}
              data-slot-id={i}
              onDragStart={actions.onDayDragStart}
              onDragEnter={
                state.draggingEvent
                  ? actions.onEventDragEnter
                  : actions.onDayDragEnter
              }
              onDragOver={actions.onEventDragOver}
              onDrop={actions.onEventDrop}
              onDragEnd={actions.onDayDragEnd}
              onClick={actions.onClickAddSlot}
            />
            {renderNowBorder(frame.date)}
          </EventTimeCell>
        );
      })}
    </Calendar>
  );
});

const Calendar = styled.div<{ maxRow: number; maxCol: number }>`
  width: inherit;
  flex: 1 1 0%;
  display: grid;
  grid-template-rows: ${(props) => `repeat(${props.maxRow}, minmax(8px, 1fr))`};
  grid-template-columns: ${(props) => `repeat(${props.maxCol}, 1fr)`};
  grid-auto-flow: column;
  position: relative;
  border-bottom: 1px solid ${gray04};
`;
const EventTimeCell = styled(TimeCell)`
  cursor: default;
`;

const StyledEventCell = styled(EventCell)`
  z-index: 2;
`;
const NewEventCell = styled(EventCell)`
  z-index: 3;
`;
const DraggingEventCell = styled(StyledEventCell)`
  user-select: none;
  pointer-events: none;
`;
const GhostEventCell = styled(StyledEventCell)`
  user-select: none;
  pointer-events: none;
  opacity: 0.5;
`;
const DragEventCell = styled(StyledEventCell)`
  user-select: none;
  pointer-events: none;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.15);
  z-index: 3;
`;
const ServiceIcon = styled(Icon)`
  width: 12px !important;
  height: 12px !important;
  color: inherit;
  margin-right: ${spacing(2)};
  :last-of-type {
    margin-right: 0;
  }
`;
const Video = styled.div`
  display: flex;
  align-items: center;
`;
const VideoIcon = styled(Icon)`
  width: 14px !important;
  height: 14px !important;
  color: inherit;
  margin-right: ${spacing(2)};
  :last-of-type {
    margin-right: 0;
  }
`;
const Address = styled.div`
  margin-left: ${spacing(2)};
`;
const VideoName = styled.div`
  margin-left: ${spacing(2)};
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;
export default Component;
